import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  useMediaQuery,
  IconButton,
  Card,
  CardContent,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-GB';
import { fetchBillDetailPnl } from '../../services/backendAPI';
import TimelineIcon from '@mui/icons-material/Timeline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { formatTimeToIST } from '../../utils/dateUtils';
import CollapsibleMobileCard from '../Common/CollapsibleMobileCard';

const PnlHistoryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Function to check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };
  
  // Function to get the most recent business day
  const getMostRecentBusinessDay = () => {
    const today = new Date();
    let recentBusinessDay = new Date(today);
    
    // If today is Monday, set to previous Friday
    if (today.getDay() === 1) { // Monday
      recentBusinessDay.setDate(today.getDate() - 3);
      return recentBusinessDay;
    }
    
    // If today is Sunday, set to previous Friday
    if (today.getDay() === 0) { // Sunday
      recentBusinessDay.setDate(today.getDate() - 2);
      return recentBusinessDay;
    }
    
    // If today is Saturday, set to previous Friday
    if (today.getDay() === 6) { // Saturday
      recentBusinessDay.setDate(today.getDate() - 1);
      return recentBusinessDay;
    }
    
    // For regular weekdays, use yesterday
    recentBusinessDay.setDate(today.getDate() - 1);
    return recentBusinessDay;
  };
  
  // Table data state
  const [pnlHistory, setPnlHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Minimum allowed date - January 1, 2025
  const MIN_DATE = new Date('2025-01-01');
  // Maximum allowed date - current date
  const MAX_DATE = new Date();
  
  // Get dates from localStorage if available, otherwise use defaults
  const getInitialStartDate = () => {
    const stored = localStorage.getItem('pnl_history_startDate');
    if (stored) {
      const date = new Date(stored);
      if (!isNaN(date.getTime())) return date;
    }
    // Default to one week before current date (but not before MIN_DATE)
    const oneWeekBefore = new Date();
    oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
    
    // If one week before is earlier than MIN_DATE, use MIN_DATE
    return oneWeekBefore < MIN_DATE ? new Date(MIN_DATE) : oneWeekBefore;
  };
  
  const getInitialEndDate = () => {
    const stored = localStorage.getItem('pnl_history_endDate');
    if (stored) {
      const date = new Date(stored);
      if (!isNaN(date.getTime())) return date;
    }
    // Default to current date
    return new Date();
  };
  
  // Get date 3 months before the given date
  const getThreeMonthsBefore = (date) => {
    const threeMonthsBefore = new Date(date);
    threeMonthsBefore.setMonth(date.getMonth() - 3);
    return threeMonthsBefore;
  };
  
  const getInitialExchange = () => {
    return localStorage.getItem('pnl_history_exchange') || 'ALL';
  };
  
  // Filter states - using default values from localStorage or fallback defaults
  const [startDate, setStartDate] = useState(() => getInitialStartDate());
  const [endDate, setEndDate] = useState(() => getInitialEndDate());
  const [exchange, setExchange] = useState(() => getInitialExchange());
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const formatDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      return format(dateObj, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };
  
  const formatDisplayDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      return format(dateObj, 'dd-MM-yyyy');
    } catch (error) {
      console.error('Error formatting display date:', error);
      return '';
    }
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0.00';
    
    const numValue = parseFloat(value);
    const absValue = Math.abs(numValue);
    const sign = numValue < 0 ? '-' : '';
    
    return `${sign}₹${absValue}`;
  };
  
  const fetchPnlData = async (newPage, newRowsPerPage, overrideParams = null) => {
    setLoading(true);
    try {
      // Determine the exchange parameter - prioritize overrideParams if provided
      let exchangeParam = '';
      if (overrideParams?.exchange !== undefined) {
        // If overrideParams has exchange, use it (even if it's empty string for 'ALL')
        exchangeParam = overrideParams.exchange;
      } else {
        // Otherwise use current exchange state
        exchangeParam = exchange === 'ALL' ? '' : exchange;
      }
      
      const params = {
        start_date: formatDate(overrideParams?.startDate || startDate),
        end_date: formatDate(overrideParams?.endDate || endDate),
        page: newPage + 1,
        page_size: newRowsPerPage,
        exchange: exchangeParam,
      };
      
      const response = await fetchBillDetailPnl(params);
      if (response && response.data) {
        // Process the bill detail data to match the format needed for PnL display
        const processedData = response.data.map(item => ({
          trade_id: item.BillID,
          bill_id: item.BillID,
          trading_symbol: item.TradingSymbol,
          qty: Math.max(item.BuyQty || 0, item.SellQty || 0),
          buy_amount: item.BuyAmount,
          sell_amount: item.SellAmount,
          buy_avg: item.BuyRate,
          sell_avg: item.SellRate,
          realized_pnl: item.PNL,
          trade_time: item.CreatedAt,
          exchange: item.Exchange,
          narration: item.Narration
        }));
        
        setPnlHistory(processedData);
        setTotalCount(response.total_count || processedData.length);
      } 
    } catch (error) {
      console.error("Error fetching PnL history:", error);
      enqueueSnackbar(error.message || 'Error fetching PnL history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPnlData(page, rowsPerPage);
  }, []);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPnlData(newPage, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchPnlData(0, newRowsPerPage);
  };
  
  const handleApplyFilters = () => {
    // Validate dates before applying filters
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      enqueueSnackbar('Please select valid start and end dates', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
      return;
    }
    
    setPage(0);
    fetchPnlData(0, rowsPerPage);
  };
  
  const handleResetFilters = () => {
    // Define the reset values - absolute defaults
    const oneWeekBefore = new Date();
    oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
    const defaultStartDate = oneWeekBefore < MIN_DATE ? new Date(MIN_DATE) : oneWeekBefore;
    const defaultEndDate = new Date();
    const defaultExchange = 'ALL';
    
    // Reset the state values
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setExchange(defaultExchange);
    setPage(0);
    
    // Clear localStorage to prevent them from coming back on reload/navigation
    localStorage.removeItem('pnl_history_startDate');
    localStorage.removeItem('pnl_history_endDate');
    localStorage.removeItem('pnl_history_exchange');
    
    // Fetch new data with reset values - use empty string for exchange when 'ALL' is selected
    fetchPnlData(0, rowsPerPage, {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      exchange: '' // Empty string for 'ALL' exchange
    });
  };
  
  // Get color for PnL values
  const getPnlColor = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return theme.palette.text.secondary;
    return numValue > 0 ? theme.palette.success.main : numValue < 0 ? theme.palette.error.main : theme.palette.text.secondary;
  };
  
  // Calculate summary data for P&L
  const summaryData = useMemo(() => {
    if (!pnlHistory || pnlHistory.length === 0) {
      return {
        totalRealizedPnL: 0
      };
    }

    return pnlHistory
      .reduce((acc, item) => {
        acc.totalRealizedPnL += parseFloat(item.realized_pnl || 0);
        return acc;
      }, { totalRealizedPnL: 0 });
  }, [pnlHistory]);

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      height: '100%',
      px: { xs: 0, sm: 1 },
      pt: { xs: 1, sm: 2 },
      pb: { xs: 0, sm: 2 }
    }}>
      <Box sx={{ flexShrink: 0 }}>
        {/* P&L Summary Card for both mobile and desktop */}
        <Card 
          sx={{ 
            mb: isMobile ? 1 : 2, 
            background: theme.palette.background.header,
            color: theme.palette.text.header,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }}
        >
        <CardContent sx={{ 
          p: isMobile ? 1.5 : 2,
          '&:last-child': { pb: isMobile ? 1.5 : 2 },
          position: 'relative'
        }}>
          {/* Background decorative elements */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0,
            opacity: 0.1
          }}>
            <Box sx={{
              position: 'absolute',
              top: '-5%',
              left: '-5%',
              width: '40%',
              height: '40%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(76,175,80,0.6) 0%, rgba(76,175,80,0) 70%)',
              display: summaryData.totalRealizedPnL >= 0 ? 'block' : 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: '-5%',
              right: '-5%',
              width: '40%',
              height: '40%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(244,67,54,0.6) 0%, rgba(244,67,54,0) 70%)',
              display: summaryData.totalRealizedPnL < 0 ? 'block' : 'none'
            }} />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            {/* Title on the left */}
            <Typography sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              color: theme.palette.text.header,
              opacity: 0.85,
              letterSpacing: 0.5,
            }}>
              Net Realised P&L
            </Typography>
            
            {/* Amount on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '1rem', sm: '1.25rem' },
                color: summaryData.totalRealizedPnL >= 0 ? theme.palette.success.main : theme.palette.error.main,
                mr: 0.5
              }}>
                ₹{Math.abs(summaryData.totalRealizedPnL).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </Typography>
              {summaryData.totalRealizedPnL !== 0 && (
                summaryData.totalRealizedPnL > 0 
                  ? <ArrowDropUpIcon sx={{ color: theme.palette.success.main, fontSize: { xs: 24, sm: 28 } }} />
                  : <ArrowDropDownIcon sx={{ color: theme.palette.error.main, fontSize: { xs: 24, sm: 28 } }} />
              )}
            </Box>
          </Box>
        </CardContent>
        </Card>
    
        {/* Filters */}
        <Paper 
          id="date-filter" 
          elevation={isMobile ? 0 : undefined} 
          sx={{ 
            p: isMobile ? '8px 12px' : 2, 
            mb: isMobile ? 1 : 3, 
            bgcolor: isMobile ? 'transparent' : 'background.paper',
            boxShadow: isMobile ? 'none' : undefined,
            backgroundImage: isMobile ? 'none' : undefined
          }}
        >
        {isMobile ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: showMobileFilters ? 2 : 0
          }}>
            <Button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              startIcon={<FilterListIcon />}
              endIcon={showMobileFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              size="small"
              sx={{ 
                color: 'text.secondary',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                }
              }}
            >
              Filter
            </Button>
          </Box>
        ) : (
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        )}
        
        <Collapse in={!isMobile || showMobileFilters}>
          <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="From"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                    setStartDate(newValue);
                    // Auto-search when From date is changed
                    setPage(0);
                    fetchPnlData(0, rowsPerPage, { startDate: newValue });
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                minDate={MIN_DATE}
                maxDate={endDate}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="To"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                    setEndDate(newValue);
                    // Auto-search when To date is changed
                    setPage(0);
                    fetchPnlData(0, rowsPerPage, { endDate: newValue });
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                minDate={startDate}
                maxDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="exchange-select-label">Exchange</InputLabel>
              <Select
                labelId="exchange-select-label"
                value={exchange}
                label="Exchange"
                onChange={(e) => {
                  const newExchange = e.target.value;
                  setExchange(newExchange);
                  // Auto-search when exchange is changed
                  setPage(0);
                  fetchPnlData(0, rowsPerPage, { exchange: newExchange === 'ALL' ? '' : newExchange });
                }}
              >
                <MenuItem value="ALL">ALL</MenuItem>
                <MenuItem value="NSE">Cash NSE</MenuItem>
                <MenuItem value="BSE">Cash BSE</MenuItem>
                <MenuItem value="NFO">Derivative NFO</MenuItem>
                <MenuItem value="BFO">Derivative BFO</MenuItem>
                <MenuItem value="MCX">Commodities</MenuItem>
                <MenuItem value="CDS">Forex</MenuItem>
                <MenuItem value="DCSX">Crypto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%'
            }}>
              <Button 
                variant="contained" 
                startIcon={<SearchIcon />} 
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={handleResetFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Reset
              </Button>
            </Box>
          </Grid>
          </Grid>
        </Collapse>
      </Paper>
      </Box>
      
      {/* Results */}
      <Paper sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        width: '100%'
      }}>
        <TableContainer 
          sx={{ 
            flex: 1,
            overflow: 'auto',
              overflowX: 'auto',
              minHeight: 0,
              willChange: 'scroll-position',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[100],
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[400],
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.grey[500],
                },
              },
            }}
          >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : pnlHistory.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              p: 6,
              textAlign: 'center'
            }}>
              <TimelineIcon sx={{ 
                fontSize: 64, 
                color: theme.palette.grey[400],
                mb: 2
              }} />
              <Typography variant="h6" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                No P&L Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                No PnL data found for the selected date range.
              </Typography>
            </Box>
          ) : (
            <>
              {isMobile ? (
                // Mobile view - Simple Box layout without Cards
                <Box sx={{ 
                  p: 2,
                  willChange: 'scroll-position',
                  WebkitOverflowScrolling: 'touch',
                }}>
                  {pnlHistory.map((item, index) => (
                    <CollapsibleMobileCard
                      key={index}
                      headerLeft={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {item.trading_symbol}
                        </Typography>
                      }
                      headerBadge={
                        <Chip 
                          label={item.exchange} 
                          size="small"
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem', 
                            fontWeight: 600,
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          }}
                        />
                      }
                      headerRight={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600,
                              color: item.realized_pnl >= 0 ? theme.palette.success.main : theme.palette.error.main
                            }}
                          >
                            {item.realized_pnl < 0 ? '-' : ''}₹{Math.abs(item.realized_pnl).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      }
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={8}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Realised P&L
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              color: item.realized_pnl >= 0 ? theme.palette.success.main : theme.palette.error.main
                            }}
                          >
                            {item.realized_pnl < 0 ? '-' : ''}₹{Math.abs(item.realized_pnl).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Qty
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {item.qty}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Buy Avg.
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            ₹{item.buy_avg}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Sell Avg.
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            ₹{item.sell_avg}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {formatDisplayDate(item.trade_time)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={7}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Bill ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {item.bill_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Trade Time
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {formatTimeToIST(item.trade_time)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CollapsibleMobileCard>
                  ))}
                </Box>
              ) : (
                // Desktop view - Table layout 
                <Table 
                  stickyHeader 
                  aria-label="sticky table" 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    tableLayout: 'auto',
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Bill ID</TableCell>
                      <TableCell>Trading Symbol</TableCell>
                      <TableCell>Exchange</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Buy Average</TableCell>
                      <TableCell>Sell Average</TableCell>
                      <TableCell>Realized PnL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pnlHistory.map((item, index) => (
                      <TableRow hover tabIndex={-1} key={`${item.trade_id}-${index}`}>
                        <TableCell>{item.bill_id}</TableCell>
                        <TableCell>{item.trading_symbol || item.instrument_id}</TableCell>
                        <TableCell>{item.exchange}</TableCell>
                        <TableCell>{item.qty}</TableCell>
                        <TableCell>{item.buy_avg > 0 ? `₹${parseFloat(item.buy_avg)}` : "-"}</TableCell>
                        <TableCell>{item.sell_avg > 0 ? `₹${parseFloat(item.sell_avg)}` : "-"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ color: getPnlColor(item.realized_pnl), fontWeight: 600 }}>
                              {formatCurrency(item.realized_pnl)}
                            </Typography>
                            {parseFloat(item.realized_pnl || 0) !== 0 && (
                              parseFloat(item.realized_pnl || 0) > 0 
                                ? <ArrowDropUpIcon sx={{ color: theme.palette.success.main }} />
                                : <ArrowDropDownIcon sx={{ color: theme.palette.error.main }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </TableContainer>
      </Paper>

      {pnlHistory.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
          sx={{
            flexShrink: 0,
            width: '100%',
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            '& .MuiToolbar-root': {
              minHeight: '40px',
              px: 1
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              margin: 0,
              fontSize: '0.8rem'
            }
          }}
        />
      )}
    </Box>
  );
};

export default PnlHistoryDetails; 